import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Landmark, Home as HomeIcon, Wallet, Banknote, Clock, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const base = [
  { icon: Wallet, title: "Finanziamenti", desc: "Supporto nella preparazione della documentazione." },
  { icon: HomeIcon, title: "Mutui", desc: "Assistenza nella raccolta dei documenti per il mutuo." },
  { icon: Landmark, title: "Cessione del quinto", desc: "Organizziamo la pratica di cessione del quinto." },
  { icon: Banknote, title: "Anticipo TFS", desc: "Assistenza per l'anticipo del Trattamento di Fine Servizio." },
];

const DELEGA_CRIF = "Delego ViviTranquillo.com (Hyper Consult SRL) a richiedere e trattare per mio conto le informazioni creditizie presso i soggetti competenti e autorizzo il trattamento e l'inoltro dei documenti necessari, secondo Privacy Policy e Termini e Condizioni.";

const DELEGA_BANCHE_DATI = "Delego ViviTranquillo.com (Hyper Consult SRL) a richiedere e trattare per mio conto le informazioni presso le banche dati consultate (CTC, Experian e Banca d'Italia) e autorizzo il trattamento e l'inoltro dei documenti necessari, secondo Privacy Policy e Termini e Condizioni.";

function PaidService({ id, service, title, subtitle, price, description, delegaText }) {
  const [open, setOpen] = useState(false);
  const [delega, setDelega] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!delega) { toast.error("Devi accettare la delega per procedere."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/services/checkout", { origin_url: window.location.origin, service, consent: true });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile avviare il pagamento");
      setLoading(false);
    }
  };

  return (
    <div data-testid={`service-card-${id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold text-[#0A192F]">{title}</h3>
          {subtitle && <p className="text-xs font-bold uppercase tracking-wide text-[#10B981]">{subtitle}</p>}
        </div>
        <span className="whitespace-nowrap rounded-full bg-[#0A192F] px-3 py-1 text-sm font-bold text-white">€{price} <span className="text-xs font-medium text-slate-300">una tantum</span></span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{description}</p>
      <div className="mt-4 flex flex-wrap gap-3 text-xs">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F8FAFC] px-3 py-1 font-semibold text-slate-600"><Clock className="h-3.5 w-3.5 text-[#10B981]" /> Esito entro massimo 30 giorni</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F8FAFC] px-3 py-1 font-semibold text-slate-600">Nessun rinnovo automatico</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F8FAFC] px-3 py-1 font-semibold text-slate-600">Separato dal Piano annuale</span>
      </div>

      {!open ? (
        <button data-testid={`service-request-${id}`} onClick={() => setOpen(true)} className="mt-5 w-full rounded-full bg-[#10B981] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#059669]">
          Acquista · €{price}
        </button>
      ) : (
        <div className="mt-5 rounded-xl border border-slate-200 bg-[#F8FAFC] p-4">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input data-testid={`service-delega-${id}`} type="checkbox" checked={delega} onChange={(e) => setDelega(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#10B981]" />
            <span>{delegaText}</span>
          </label>
          <p className="mt-3 text-xs text-slate-500">Dopo il pagamento ti verrà richiesto di allegare documento d'identità (fronte e retro) e tessera sanitaria.</p>
          <div className="mt-4 flex gap-3">
            <button data-testid={`service-confirm-${id}`} onClick={submit} disabled={loading || !delega} className="flex-1 rounded-full bg-[#10B981] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
              {loading ? "Attendere..." : `Paga €${price}`}
            </button>
            <button onClick={() => { setOpen(false); setDelega(false); }} className="rounded-full border-2 border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AreaCredito() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);

  // Return from Stripe checkout for a credit service: confirm payment server-side, then open the pratica.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const kind = params.get("kind");
    if (params.get("canceled")) {
      toast.info("Pagamento annullato.");
      params.delete("canceled"); setParams(params, { replace: true });
      return;
    }
    if (!sessionId || kind !== "service") return;
    let cancelled = false;
    setVerifying(true);
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (cancelled) return;
          setVerifying(false);
          toast.success("Pagamento confermato! Allega i documenti richiesti per avviare la pratica.");
          params.delete("session_id"); params.delete("kind"); setParams(params, { replace: true });
          if (data.request_id) navigate(`/area/pratiche/${data.request_id}`);
          else navigate("/area/pratiche");
          return;
        }
      } catch { /* ignore */ }
      if (tries >= 8) {
        if (!cancelled) {
          setVerifying(false);
          toast.info("Stiamo confermando il pagamento. La pratica comparirà a breve nelle tue Pratiche.");
          params.delete("session_id"); params.delete("kind"); setParams(params, { replace: true });
        }
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="area-credito-page" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Credito</h1>
        <p className="mt-1 text-slate-600">Supporto credito per abbonati e servizi di controllo una tantum.</p>
      </div>

      {verifying && (
        <div data-testid="credito-verifying" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <Loader2 className="h-5 w-5 animate-spin" /> Stiamo confermando il tuo pagamento con Stripe...
        </div>
      )}

      <div>
        <h2 className="font-display text-lg font-bold text-[#0A192F]">Supporto credito (Piano ViviTranquillo)</h2>
        <p className="mt-1 text-sm text-slate-500">Ti affianchiamo nella raccolta e verifica dei documenti e inoltriamo la richiesta al reparto competente.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {base.map((s) => (
            <div key={s.title} data-testid={`credito-base-${s.title.toLowerCase().replace(/[^a-z]/g, "")}`} className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#0A192F]/5"><s.icon className="h-5 w-5 text-[#0A192F]" /></span>
              <div className="flex-1">
                <p className="font-semibold text-[#0A192F]">{s.title}</p>
                <p className="text-sm text-slate-500">{s.desc}</p>
                <button onClick={() => navigate("/area/richieste")} className="mt-2 text-sm font-semibold text-[#10B981]">Apri una richiesta →</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-lg font-bold text-[#0A192F]">Servizi di controllo (una tantum)</h2>
        <p className="mt-1 text-sm text-slate-500">Servizi a pagamento singolo, separati dal Piano annuale e dalle 3 richieste incluse.</p>
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <PaidService id="crif" service="controllo_crif" title="Controllo CRIF" price={49} delegaText={DELEGA_CRIF}
            description="Servizio di assistenza per la richiesta e verifica delle informazioni creditizie disponibili tramite i canali e i soggetti competenti. CRIF ha un tempo massimo di 30 giorni dal ricevimento della documentazione completa per fornire il riscontro." />
          <PaidService id="banchedati" service="controllo_banche_dati" title="Controllo Banche Dati Completo" subtitle="CTC · Experian · Banca d'Italia" price={99} delegaText={DELEGA_BANCHE_DATI}
            description="Servizio di assistenza per una verifica più completa delle informazioni creditizie attraverso le banche dati e i canali previsti, inclusi CTC, Experian e le informazioni pertinenti disponibili tramite Banca d'Italia, secondo autorizzazioni, consensi e requisiti applicabili." />
        </div>
      </div>
    </div>
  );
}
